import React from "react";
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  VStack,
  Divider,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import FaqSection from "../FaqSection";
import { medicalWebPageSchema, serviceSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

// Rendered by <FaqSection> and emitted as FAQPage JSON-LD by <Seo>.
// Keep the two in sync by only editing this array.
const FAQS = [
  {
    q: "How long do I need to wear braces?",
    a: "The length of treatment typically ranges from 18 months to 3 years, depending on the complexity of your case.",
  },
  {
    q: "Are braces painful?",
    a: "Braces may cause mild discomfort, especially after adjustments, but this usually subsides within a few days.",
  },
  {
    q: "Can I eat normally with braces?",
    a: "You can eat most foods, but you should avoid sticky, hard, or chewy foods that could damage the braces.",
  },
  {
    q: "How often do I need to visit the orthodontist with braces?",
    a: "You will typically visit the orthodontist every 4-6 weeks for adjustments.",
  },
  {
    q: "Can I brush and floss with braces?",
    a: "Yes, but it may take more time and effort. Your orthodontist will provide tips for cleaning around the brackets and wires.",
  },
  {
    q: "Are clear aligners as effective as braces?",
    a: "Clear aligners can be effective for mild to moderate orthodontic issues, but for more complex cases, traditional braces may be necessary.",
  },
  {
    q: "Can I play sports with braces?",
    a: "Yes, but it’s recommended to wear a mouthguard to protect your braces and teeth from injury.",
  },
  {
    q: "Can I whiten my teeth with braces?",
    a: "It’s best to wait until your braces are removed before whitening your teeth, as the brackets can cause uneven whitening.",
  },
  {
    q: "Can I get braces if I have dental fillings or crowns?",
    a: "Yes, braces can be applied to teeth with fillings or crowns, although your orthodontist may need to adjust the treatment plan.",
  },
  {
    q: "Will I need to wear a retainer after getting my braces removed?",
    a: "Yes, wearing a retainer is essential to prevent your teeth from shifting back to their original position.",
  },
];

const PAGE_PATH = "/braces-and-aligners";
const PAGE_URL = absoluteUrl(PAGE_PATH);

const BracesAndAligners = () => {
  return (
    <Box bg="#fceee5">
      <Seo
        path={PAGE_PATH}
        title="Braces & Clear Aligners in Jaipur | Teeth Straightening"
        description="Straighten your teeth with braces and invisible aligners in Vaishali Nagar, Jaipur. Customised orthodontic treatment plans at Qualiteeth Dental Care."
        type="article"
        faqs={FAQS}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Braces and Aligners", path: PAGE_PATH },
        ]}
        schemas={[
          medicalWebPageSchema({
            url: PAGE_URL,
            title: "Braces & Clear Aligners in Jaipur | Teeth Straightening",
            description: "Straighten your teeth with braces and invisible aligners in Vaishali Nagar, Jaipur. Customised orthodontic treatment plans at Qualiteeth Dental Care.",
            procedureName: "Orthodontic Treatment with Braces and Aligners",
          }),
          serviceSchema({
            url: PAGE_URL,
            name: "Orthodontic Treatment with Braces and Aligners",
            description: "Straighten your teeth with braces and invisible aligners in Vaishali Nagar, Jaipur. Customised orthodontic treatment plans at Qualiteeth Dental Care.",
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          Braces and Aligners: Everything You Need to Know
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="normal" mb={4}>
          Braces and clear aligners are orthodontic appliances that move crooked, crowded or gapped teeth into a correct, functional alignment. Fixed braces use brackets and wires; aligners are a series of removable, near-invisible trays. Both correct the bite as well as the appearance of the smile.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          By Dr. Aishwarya Sarda, MDS — Conservative Dentistry & Endodontics
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What Are Dental Braces?
            </Heading>
            <Text mt={2} fontWeight="light">
              Dental braces are orthodontic devices used to straighten and align
              teeth. They consist of a series of brackets, wires, and other
              components that gradually shift the position of your teeth over
              time. Braces are commonly used to correct crooked teeth,
              misaligned bites, and other dental issues, improving both
              appearance and functionality. They can be worn by children, teens,
              and adults, making them a versatile solution for a wide range of
              dental concerns.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dental Braces Uses
            </Heading>
            <Text mt={2} fontWeight="light">
              Dental braces are primarily used to:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Correct Crooked Teeth:</strong> Braces help straighten
                  teeth that are misaligned.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Fix Overbite and Underbite:</strong> They correct bite
                  issues, where the upper and lower teeth do not meet properly.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Improve Chewing and Speaking:</strong> Misaligned
                  teeth can affect your ability to chew and speak clearly.
                  Braces can help improve both.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Prevent Dental Issues:</strong> Properly aligned teeth
                  are easier to clean and maintain, reducing the risk of tooth
                  decay, gum disease, and other oral health issues.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Enhance Smile Aesthetics:</strong> Braces are a
                  popular option for improving the appearance of the teeth,
                  making them look straighter and more aligned.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Types of Braces
            </Heading>
            <Text mt={2} fontWeight="light">
              There are several types of braces, each with its advantages and
              considerations:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Traditional Metal Braces:</strong> These are the most
                  common type of braces. They consist of metal brackets that are
                  attached to each tooth and connected by wires. They are the
                  most noticeable option but are also the most effective.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Ceramic Braces:</strong> These are similar to metal
                  braces but use clear or tooth-colored brackets, making them
                  less noticeable. Ceramic braces are popular among teenagers
                  and adults who are looking for a more discreet option.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Lingual Braces:</strong> These braces are attached to
                  the back of the teeth, making them invisible from the outside.
                  They are custom-made to fit the contours of your teeth but may
                  be more difficult to clean.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Clear Aligners (e.g., Invisalign):</strong> Clear
                  aligners are removable trays that gradually move your teeth
                  into place. They are a popular choice for adults and teens who
                  want a more discreet option and have mild to moderate
                  orthodontic issues.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Metal Braces vs Ceramic Braces
            </Heading>
            <Text mt={2} fontWeight="light">
              Both metal and ceramic braces have their advantages, but there are
              key differences to consider:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Appearance:</strong> Ceramic braces are more discreet
                because they are made of clear or tooth-colored materials,
                whereas metal braces are more noticeable.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Durability:</strong> Metal braces are generally more
                durable and less prone to breaking compared to ceramic braces,
                which can be more fragile.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Cost:</strong> Metal braces are usually more affordable
                than ceramic braces due to the difference in material and
                manufacturing costs.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Comfort:</strong> Some people find that ceramic braces
                cause more irritation to the gums, although this varies from
                person to person.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Treatment Time:</strong> Both metal and ceramic braces
                can be effective in treating a wide range of dental issues.
                However, metal braces may sometimes work slightly faster due to
                their strength.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dental Braces Benefits
            </Heading>
            <Text mt={2} fontWeight="light">
              The benefits of dental braces are numerous, including:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Improved Smile:</strong> Braces straighten teeth,
                improving the overall appearance of your smile.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Better Oral Health:</strong> By aligning your teeth,
                braces make it easier to clean and maintain them, reducing the
                risk of cavities, gum disease, and tooth wear.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Enhanced Confidence:</strong> A straighter smile can
                help boost self-esteem and confidence.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Corrected Bite:</strong> Braces can fix bite issues like
                overbite, underbite, and crossbite, which can improve chewing,
                speaking, and overall dental function.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Long-Term Benefits:</strong> Braces not only improve
                aesthetics but also promote better long-term oral health by
                addressing underlying dental issues.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Risks of Not Getting Braces
            </Heading>
            <Text mt={2} fontWeight="light">
              Failing to get braces when necessary can result in various dental
              problems, including:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Crooked Teeth:</strong> Misaligned teeth can make it
                difficult to clean teeth properly, leading to cavities and gum
                disease.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Jaw Pain:</strong> Bite issues can lead to jaw pain,
                headaches, and difficulty chewing.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Worsening of Dental Problems:</strong> Conditions such
                as an overbite or underbite can worsen over time if left
                untreated.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Reduced Confidence:</strong> Crooked or misaligned teeth
                may cause self-consciousness and affect your smile.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Braces Age Limit
            </Heading>
            <Text mt={2} fontWeight="light">
              While braces are often associated with adolescents, there is no
              strict age limit for getting braces. In fact, more adults are
              seeking orthodontic treatment than ever before. Generally,
              orthodontic treatment is most effective when the teeth and jaw are
              still developing, but adults can also benefit from braces,
              especially with options like clear aligners. Children typically
              begin orthodontic treatment between the ages of 9 and 14, while
              adults can start treatment at any age, as long as their teeth and
              gums are healthy.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              When to Visit a Dentist for Braces
            </Heading>
            <Text mt={2} fontWeight="light">
              It’s essential to visit a dentist or orthodontist if you
              experience any of the following:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Crooked or Crowded Teeth:</strong> If your teeth are
                misaligned, braces can help straighten them.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Bite Issues:</strong> If your teeth don’t meet properly
                when you bite, you may need braces to correct overbite,
                underbite, or crossbite.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Difficulty Chewing or Speaking:</strong> Misaligned
                teeth can make it hard to chew food or speak clearly, which can
                be corrected with braces.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Jaw Pain or Headaches:</strong> Bite misalignments can
                cause jaw pain and headaches, which braces can help address.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              A consultation with an orthodontist will help determine if braces
              are right for you and what type of braces will best suit your
              needs.
            </Text>
          </Box>

          <Divider />

          <FaqSection items={FAQS} />
        </VStack>
      </Box>
    </Box>
  );
};

export default BracesAndAligners;
